/**
 * The clean-up of superseded and past-day estimate observations (`Rws.AiInsights.Host.Reporting.ObservationPurgeAdminEndpoints`).
 * Administration-only: every call needs `settings.write`, and answers `denied` without it.
 */
import { adminRequest, type AdminResult } from "./adminApi";

/** How many past-day estimate rows a purge would remove now. This is a quick count. */
export interface PastEstimateCount {
  pastEstimateRows: number;
}

/** How many superseded rows a purge would remove now. This scans the whole table, so it is requested on demand. */
export interface SupersededCount {
  supersededRows: number;
}

/** How many rows one purge removed. */
export interface ObservationPurgeResult {
  rowsDeleted: number;
}

const purgePath = "/api/reporting/observation-purge";

/** Calls `GET /api/reporting/observation-purge`: counts the past-day estimates, without deleting anything. */
export async function fetchPastEstimateCount(): Promise<AdminResult<PastEstimateCount>> {
  return adminRequest<PastEstimateCount>(purgePath, { method: "GET" }, "past estimate count");
}

/** Calls `GET /api/reporting/observation-purge/superseded-count`: counts the superseded rows. Slow on the stored history. */
export async function fetchSupersededCount(): Promise<AdminResult<SupersededCount>> {
  return adminRequest<SupersededCount>(`${purgePath}/superseded-count`, { method: "GET" }, "superseded count");
}

/** Calls `POST /api/reporting/observation-purge/superseded`: deletes every row a newer fetch of the same figure replaces. */
export async function purgeSupersededObservations(): Promise<AdminResult<ObservationPurgeResult>> {
  return adminRequest<ObservationPurgeResult>(`${purgePath}/superseded`, { method: "POST" }, "purge superseded data");
}

/** Calls `POST /api/reporting/observation-purge/past-estimates`: deletes the estimates for every earlier day, then rebuilds the totals. */
export async function purgePastEstimates(): Promise<AdminResult<ObservationPurgeResult>> {
  return adminRequest<ObservationPurgeResult>(`${purgePath}/past-estimates`, { method: "POST" }, "remove past-day estimates");
}
